<template>
  <div class="tw-relative" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave" @click.stop>
    <!-- Usage count pill -->
    <div class="tw-flex tw-cursor-default tw-flex-col tw-gap-2 tw-rounded-full tw-bg-orange-50 tw-p-2">
      <div class="tw-flex tw-gap-1 tw-text-2xs tw-text-deep-orange-600">
        <TablerLoader2 v-if="usageLoading" class="tw-h-4 tw-w-4 tw-animate-spin" />
        <TablerBroadcast v-else class="tw-h-4 tw-w-4" />
        <span>{{ videoAsset.usageCount }}</span>
      </div>
    </div>

    <!-- Usage popover -->
    <Transition
      enterActiveClass="tw-transition tw-duration-200 tw-ease-out"
      enterFromClass="tw-transform tw-scale-95 tw-opacity-0"
      enterToClass="tw-transform tw-scale-100 tw-opacity-100"
      leaveActiveClass="tw-transition tw-duration-150 tw-ease-in"
      leaveFromClass="tw-transform tw-scale-100 tw-opacity-100"
      leaveToClass="tw-transform tw-scale-95 tw-opacity-0"
    >
      <div
        v-if="showUsagePopover && usageData && usageData.length > 0"
        class="tw-absolute tw-z-50 tw-flex tw-max-h-[200px] tw-w-64 tw-cursor-default tw-flex-col tw-gap-2 tw-overflow-y-auto tw-rounded-md tw-border tw-bg-white tw-py-4 tw-shadow-md"
        :class="{ 'tw-bottom-8 tw-mt-1': tooltipDirection === 'up', 'tw-top-8 tw-mb-1': tooltipDirection === 'down' }"
      >
        <!-- Error state -->
        <div v-if="usageFetchError" class="tw-flex tw-flex-col tw-gap-2">
          <span class="tw-text-xs tw-text-red-500">Error fetching usage data</span>
        </div>

        <!-- Usage data -->
        <div v-else-if="usageData" class="tw-flex tw-flex-col tw-gap-2">
          <div class="tw-px-4 tw-text-sm tw-font-medium">Used In</div>
          <ul class="tw-space-y-2 tw-text-xs">
            <li v-for="usage in usageData" :key="usage.entity_id">
              <button
                :disabled="!usage.entity_id"
                class="tw-flex tw-w-full tw-cursor-pointer tw-px-4 tw-py-2 hover:tw-bg-slate-50"
                @click.stop="onUsageClick(usage)"
              >
                <div
                  v-if="usage.entity_id"
                  :title="usage.entity_title"
                  class="tw-flex tw-w-full tw-flex-col tw-items-start"
                >
                  <span class="tw-block tw-w-full tw-truncate tw-text-left">
                    {{ usage.entity_title }}
                  </span>
                  <span
                    v-if="usage.entity_title"
                    class="tw-block tw-w-full tw-truncate tw-text-left tw-text-2xs tw-text-slate-500"
                  >
                    {{ usage.entity_subtype || transformVideoAssetUsageEntityType(usage) }}
                  </span>
                </div>
                <div v-else class="tw-flex tw-w-full tw-flex-col tw-items-start">
                  <span class="tw-block tw-w-full tw-truncate tw-text-left tw-text-2xs tw-text-slate-500">DELETED</span>
                </div>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { listVideoAssetUsage, VideoAssetUsage } from '@goldcast/api/backend';
import { ref } from 'vue';

import { redirectToUsage, transformVideoAssetUsageEntityType } from '../utils';
import { VideoAsset } from '../videoLibraryService';

import TablerBroadcast from '@/studioComponents/Icons/tabler/TablerBroadcast.vue';
import TablerLoader2 from '@/studioComponents/Icons/tabler/TablerLoader2.vue';

const props = defineProps<{
  videoAsset: VideoAsset;
  tooltipDirection: 'up' | 'down';
}>();

const usageData = ref<VideoAssetUsage[] | null>(null);
const usageLoading = ref(false);
const usageFetchError = ref<Error | null>(null);

const showUsagePopover = ref(false);
const isHovered = ref(false);

const onMouseEnter = () => {
  isHovered.value = true;
  if (usageData.value) {
    showUsagePopover.value = true;
    return;
  }

  usageLoading.value = true;
  listVideoAssetUsage({
    id: props.videoAsset.id
  })
    .then(response => {
      usageData.value = response;
      if (usageData.value && usageData.value.length > 0 && isHovered.value) {
        showUsagePopover.value = true;
      }
    })
    .catch(error => {
      usageFetchError.value = error;
    })
    .finally(() => {
      usageLoading.value = false;
    });
};

const onMouseLeave = () => {
  isHovered.value = false;
  showUsagePopover.value = false;
};

const onUsageClick = (usage: VideoAssetUsage): void => {
  redirectToUsage(usage);
};
</script>
